import React from "react"
import { Card, Divider, Form, Message } from "semantic-ui-react"
import CheckboxWithLabelForForm from "../ui/CheckboxWithLabelForForm"
import { PasswordInput } from "../ui/Admin/Form/PasswordInput"
import api2 from "~/api2"
import { z } from "zod"
import { Controller } from "react-hook-form"
import useAppForm from "~/hooks/useAppForm"
import { usePasswordRules, passwordSchema } from "~/hooks/usePasswordRules"

interface componentProps {
  campSlug: string
  setErrorMessage: (arg: string) => void
  token?: string
}

const SignupSchema = z
  .object({
    password: passwordSchema,
    passwordConfirmation: z.string(),
    isCheckedTerms: z.boolean(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: "Passwords do not match",
    path: ["passwordConfirmation"],
  })
  .refine((data) => data.isCheckedTerms === true, {
    message: "You must accept the terms of service and privacy policy.",
    path: ["isCheckedTerms"],
  })

type SignupFormType = z.infer<typeof SignupSchema>

const SignupForm = (props: componentProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid, isSubmitting, touchedFields },
    setError,
  } = useAppForm<SignupFormType>({
    defaultValues: {
      password: "",
      passwordConfirmation: "",
      isCheckedTerms: false,
    },
    schema: SignupSchema,
    mode: "onChange",
  })

  const onSubmit = async (data: SignupFormType) => {
    try {
      await api2.Auth.adminSignupConfirmation(data.password, data.passwordConfirmation, props.token)
      window.location.href = `${window.origin}/admin/login`
      props.setErrorMessage("")
    } catch (errResponse: any) {
      props.setErrorMessage(errResponse.message["error"].join(".\n "))
    }
  }

  return (
    <Card fluid>
      <Card.Content>
        <Card.Header>{"Accept invitation"}</Card.Header>
        <Card.Meta>{"Set a password for your account. You will be logged in after setting your password."}</Card.Meta>

        <Divider />

        <Form onSubmit={handleSubmit(onSubmit)} loading={isSubmitting}>
          <Controller
            name="password"
            control={control}
            render={({ field, fieldState }) => {
              const value = field.value || ""
              const showError = touchedFields.password && isDirty && value.length > 0 && !errors.password
              const showRules = touchedFields.password && isDirty && value.length > 0
              const [failedRules, rules] = usePasswordRules(value)
              return (
                <div>
                  <PasswordInput
                    {...field}
                    label="Password"
                    required
                    icon={
                      value && failedRules.length === 0
                        ? { name: "check circle", color: "green" }
                        : { name: "key" }
                    }
                    placeholder="Password"
                    error={false}
                  />
                  {showRules && (
                    <div style={{ marginTop: 4 }}>
                      <ul style={{ paddingLeft: 18, margin: 0 }}>
                        {rules.map(rule => (
                          <li
                            key={rule.message}
                            style={{ color: rule.test(value) ? "green" : "#b4710a", fontSize: 13, listStyleType: 'disc' }}
                          >
                            {rule.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            }}
          />

          <Controller
            name="passwordConfirmation"
            control={control}
            render={({ field }) => {
              const value = field.value || ""
              const showError = touchedFields.passwordConfirmation && isDirty && value.length > 0 && errors.passwordConfirmation
              return (
                <div>
                  <PasswordInput
                    {...field}
                    label="Password Confirmation"
                    required
                    icon={
                      value && !errors.passwordConfirmation
                        ? { name: "check circle", color: "green" }
                        : { name: "key" }
                    }
                    placeholder="Retype password"
                    error={false}
                  />
                  {showError && (
                    <Message negative size="tiny" content={errors.passwordConfirmation?.message as string} />
                  )}
                </div>
              )
            }}
          />

          <div className="my-8">
            <Controller
              name="isCheckedTerms"
              control={control}
              render={({ field }) => (
                <CheckboxWithLabelForForm
                  label={
                    <span className="font-bold text-mdbase">
                      I acknowledge that I have reviewed and accept our{" "}
                      <a
                        className="underline text-blue-500"
                        href="https://www.poweredbypark.com/terms-and-conditions"
                        target="_blank"
                      >
                        Terms of Service
                      </a>{" "}
                      and consent to the storing and processing of your personal data as described in our{" "}
                      <a
                        className="underline text-blue-500"
                        href="https://www.poweredbypark.com/privacy-policy"
                        target="_blank"
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  }
                  styles={{ width: 25, height: 25 }}
                  value={field.value}
                  setValue={field.onChange}
                  required
                />
              )}
            />
            {errors.isCheckedTerms && <Message negative content={errors.isCheckedTerms.message as string} />}
          </div>

          <Form.Button
            fluid
            primary
            disabled={!isDirty || !isValid || isSubmitting}
          >
            {"Set password"}
          </Form.Button>
        </Form>
      </Card.Content>
    </Card>
  )
}

export default SignupForm
